import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import ArrowRightAltSharpIcon from '@gamma/ui/icons/ArrowRightAltSharpIcon';
import { ChatGPTIcon } from '../ChatGPTIcon';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { ROUTES } from '@investigator/shared/constants';
import { useAgenticTriageSettings } from '../../hooks';
import { isChatGPTEnabled } from '../../utils';

const {
  agenticInvestigationsIntroHeader,
  agenticInvestigationsIntroDescription1,
  agenticInvestigationsIntroDescription2,
  enableGPTButton,
} = i18n.pages.securityOverview.panels.highestSeverityAlertCategories;

const { systemSettingsIntegrations } = ROUTES;

const chatGPTPrivateDataConfigurationRoute = `${systemSettingsIntegrations}/chatgpt-private-data/configuration`;

export const AgenticTriageBanner: React.FC = () => {
  const { agenticTriageFeatureEnabled } = useAgenticTriageSettings();
  const { userLicense, userPrivileges } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isBannerDismissed, setIsBannerDismissed] = useState(true); // Initializing to true prevents flicker on mount.

  const tenantId = userLicense?.tenant ?? '';
  const isGPTEnabled = useMemo(
    () => isChatGPTEnabled(userPrivileges, tenantId),
    [userPrivileges, tenantId],
  );

  const handleBannerClose = () => {
    setIsBannerDismissed(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem('agenticTriageBannerDismissed', 'true');
    }
  };
  const handleEnableGPT = () => {
    navigate(chatGPTPrivateDataConfigurationRoute);
  };

  useEffect(() => {
    // ensures SSR compatibility while avoiding the banner briefly appearing before the check completes.
    if (typeof window !== 'undefined') {
      setIsBannerDismissed(
        localStorage.getItem('agenticTriageBannerDismissed') === 'true',
      );
    }
  }, []);

  if (!agenticTriageFeatureEnabled || isBannerDismissed) {
    return null;
  }

  return (
    <Box
      role="banner"
      mb={4}
      borderRadius="8px"
      position="relative"
      // Gradient border effect using padding and background
      p="2px"
      bgGradient="linear-gradient(90deg, blue.600 0%, purple.500 100%)"
    >
      <Box
        bg="layer.0"
        color={'white'}
        borderRadius="8px"
        p={6}
        position="relative"
        bgGradient="linear(90deg, blue.800 0%, purple.800 100%)"
      >
        <HStack justifyContent="space-between" alignItems="flex-start" mb={4}>
          <HStack spacing={3}>
            <ChatGPTIcon boxSize={8} />
            <Text textStyle="h4" fontWeight="bold">
              {agenticInvestigationsIntroHeader}
            </Text>
          </HStack>
          <IconButton
            aria-label="Close banner"
            icon={<CloseSharpIcon />}
            variant="ghost"
            color={'white'}
            size="sm"
            onClick={handleBannerClose}
            alignSelf="flex-start"
          />
        </HStack>
        <VStack alignItems="start" spacing={2}>
          <Text textStyle="body-md">
            {agenticInvestigationsIntroDescription1}
          </Text>
          {isGPTEnabled ? (
            <Text textStyle="body-md">
              {agenticInvestigationsIntroDescription2}
            </Text>
          ) : (
            <Button
              variant="solid"
              colorScheme="blue"
              onClick={handleEnableGPT}
              rightIcon={<ArrowRightAltSharpIcon />}
              data-testid="enable-gpt-button"
            >
              {enableGPTButton}
            </Button>
          )}
        </VStack>
      </Box>
    </Box>
  );
};
